import { Address } from '@shared/types/account/Address';
import { Address as EntityAddress } from '@/types/entity/address';

// EWM-640 kept the organisation and the person in separate fields end-to-end:
// `company` is the org (CT `company`), `firstName`/`lastName` are the person.
//
// EWM-950 narrows that: an address is identified by its ORGANISATION only.
// - the READ path (mapAddress) still surfaces `firstName`/`lastName`, because
//   historic order snapshots must render exactly as the carrier received them.
// - the WRITE path (mapCoCoAddress) no longer emits a person at all, so a
//   contact name cannot reach a saved address or an order's shipping/billing.
export const mapAddress = ({
  addressId,
  firstName,
  lastName,
  company,
  streetName,
  streetNumber,
  building,
  additionalStreetInfo,
  city,
  state,
  postalCode,
  country,
  isDefaultBillingAddress,
  isDefaultShippingAddress,
  isBillingAddress,
  isShippingAddress,
}: Partial<Address>): EntityAddress => {
  return {
    id: addressId as string,
    company,
    // Read-side only: preserved so an order snapshot placed before EWM-950 still
    // displays the contact the carrier actually saw (AC 8). Never written back.
    firstName,
    lastName,
    // EWM-950 — single-line display label is the ORG. No person fallback: an
    // address without a company renders no name line at all, rather than
    // silently promoting a contact name into the label.
    name: company?.trim() || '',
    streetName: streetName ?? '',
    streetNumber: streetNumber ?? '',
    building: building ?? '',
    line2: additionalStreetInfo ?? '',
    city,
    state,
    zip: postalCode,
    country: country ?? '',
    isDefaultBilling: isDefaultBillingAddress,
    isDefaultShipping: isDefaultShippingAddress,
    isBillingAddress,
    isShippingAddress,
  };
};

// EWM-950 — the write path. Emits the org and never a person, so neither a saved
// address nor an order's shipping/billing address can carry a contact name.
// `company` is taken verbatim (trimmed) and NOTHING falls back to it — see the
// note on the `company` field below for why the display-side `name` fallback
// must not be used on a write.
export const mapCoCoAddress = ({
  id,
  company,
  line1,
  line2,
  city,
  state,
  zip,
  country,
  streetName,
  streetNumber,
  isDefaultBilling,
  isDefaultShipping,
  isBillingAddress,
  isShippingAddress,
  building,
  apartment,
}: Partial<EntityAddress>): Address => {
  return {
    addressId: id,
    // EWM-950 — the WRITE path takes `company` and nothing else. It must NOT go
    // through `addressNameLine`, whose `name` fallback exists for DISPLAY: `name`
    // is a derived label, and an entity built by hand — or serialised before the
    // mapper changed — can still hold a person there. Routing the write through
    // it would put a contact name straight into CT's `company` field, silently
    // doing the exact thing this ticket forbids.
    company: company?.trim() || undefined,
    streetName: streetName || line1,
    streetNumber: streetNumber ?? '',
    additionalAddressInfo: line2,
    building: building ?? '',
    apartment: apartment ?? '',
    city,
    state,
    postalCode: zip,
    country,
    isDefaultBillingAddress: isDefaultBilling,
    isDefaultShippingAddress: isDefaultShipping,
    isBillingAddress,
    isShippingAddress,
  };
};
